import { useMemo } from 'react'

import useSWR from 'swr'

import {
  basketFetcher,
  type BasketContentBlock,
  type BasketInput,
  type BasketResponse,
  type CheckoutState,
} from '@library/api-menu-experience'

type GroupedBasketRecipe = {
  id: string
  quantity: number
  numPortions: number
}

type UseGroupedBasketContentInput = {
  basketDate?: string
  basketGifts: Record<string, number>
  basketProductQuantities: Record<string, number>
  basketRecipes: GroupedBasketRecipe[]
  deliverySlotId?: string
  menuUuid?: string | null
  numPortions: number
  orderId?: string
  postcode?: string
  shippingAddressId?: string
}

export type GroupedBasketContentResult = {
  checkout?: CheckoutState
  content?: BasketContentBlock[]
  error?: unknown
  isLoading: boolean
  isValidating: boolean
}

type GroupedBasketKey = readonly ['grouped-basket-content', BasketInput]

const fetchGroupedBasketContent = ([, input]: GroupedBasketKey): Promise<BasketResponse> =>
  basketFetcher(input)

const normalizeBasketProducts = (
  basketProductQuantities: Record<string, number>,
  basketGifts: Record<string, number>,
) => {
  const productQuantities = new Map<string, number>()

  Object.entries(basketProductQuantities).forEach(([id, quantity]) => {
    productQuantities.set(id, quantity)
  })
  Object.entries(basketGifts).forEach(([id, quantity]) => {
    productQuantities.set(id, (productQuantities.get(id) ?? 0) + quantity)
  })

  return Array.from(productQuantities.entries())
    .filter(([, quantity]) => quantity > 0)
    .map(([id, quantity]) => ({ id, quantity }))
    .sort((first, second) => first.id.localeCompare(second.id))
}

export const useFetchBasketContent = ({
  basketDate,
  basketGifts,
  basketProductQuantities,
  basketRecipes,
  deliverySlotId,
  menuUuid,
  numPortions,
  orderId,
  postcode,
  shippingAddressId,
}: UseGroupedBasketContentInput): GroupedBasketContentResult => {
  const input = useMemo<BasketInput | null>(() => {
    if (!menuUuid || !basketDate) {
      return null
    }

    return {
      menuId: menuUuid,
      numPortions,
      deliveryDate: basketDate,
      ...(postcode ? { postcode } : {}),
      ...(deliverySlotId ? { deliverySlotId } : {}),
      ...(shippingAddressId ? { shippingAddressId } : {}),
      ...(orderId ? { orderId } : {}),
      products: normalizeBasketProducts(basketProductQuantities, basketGifts),
      recipes: basketRecipes
        .filter(({ quantity }) => quantity > 0)
        .map(({ id, numPortions: recipeNumPortions, quantity }) => ({
          id,
          numPortions: recipeNumPortions,
          quantity,
        })),
    }
  }, [
    basketDate,
    basketGifts,
    basketProductQuantities,
    basketRecipes,
    deliverySlotId,
    menuUuid,
    numPortions,
    orderId,
    postcode,
    shippingAddressId,
  ])

  const key: GroupedBasketKey | null = input ? ['grouped-basket-content', input] : null
  const {
    data,
    error,
    isLoading: isSWRLoading,
    isValidating,
  } = useSWR(key, fetchGroupedBasketContent, {
    keepPreviousData: true,
  })

  return {
    checkout: data?.checkout,
    content: data?.content,
    error,
    isLoading: isSWRLoading && !data,
    isValidating,
  }
}
